import { doc, setDoc, getDoc, arrayUnion } from 'firebase/firestore';
import { db } from './firebase';

export interface CustomValues {
  subjects: string[];
  topics: string[];
  yearGroups: string[];
  years: string[];
  examConditions: string[];
  assessmentTypes: string[];
}

const DEFAULT_CUSTOM_VALUES: CustomValues = {
  subjects: [],
  topics: [],
  yearGroups: [],
  years: [],
  examConditions: [],
  assessmentTypes: []
};

/**
 * Get custom values for a user from Firestore
 */
export const getCustomValues = async (userId: string): Promise<CustomValues> => {
  try {
    const customValuesRef = doc(db, 'customValues', userId);
    const customValuesSnap = await getDoc(customValuesRef);
    
    if (customValuesSnap.exists()) {
      const data = customValuesSnap.data();
      return {
        subjects: data.subjects || [],
        topics: data.topics || [],
        yearGroups: data.yearGroups || [],
        years: data.years || [],
        examConditions: data.examConditions || [],
        assessmentTypes: data.assessmentTypes || []
      };
    } else {
      // Create default document if it doesn't exist
      await setDoc(customValuesRef, DEFAULT_CUSTOM_VALUES);
      return DEFAULT_CUSTOM_VALUES;
    }
  } catch (error) {
    console.error('Error fetching custom values:', error);
    return DEFAULT_CUSTOM_VALUES;
  }
};

/**
 * Add a custom value to a specific category for a user
 */
export const addCustomValue = async (userId: string, category: keyof CustomValues, value: string): Promise<void> => {
  try {
    const customValuesRef = doc(db, 'customValues', userId);

    // setDoc with merge creates the document if it doesn't exist yet (updateDoc
    // would fail with "No document to update"); arrayUnion de-duplicates the value.
    await setDoc(customValuesRef, {
      [category]: arrayUnion(value)
    }, { merge: true });
  } catch (error) {
    console.error(`Error adding custom ${category} value:`, error);
    throw error;
  }
};

/**
 * Set all custom values for a user (used for initial sync)
 */
export const setCustomValues = async (userId: string, customValues: CustomValues): Promise<void> => {
  try {
    const customValuesRef = doc(db, 'customValues', userId);
    await setDoc(customValuesRef, customValues, { merge: true });
  } catch (error) {
    console.error('Error setting custom values:', error);
    throw error;
  }
};

/**
 * Extract custom values from existing assessments and merge with stored values
 */
export const extractAndMergeCustomValues = (assessments: any[], storedCustomValues: CustomValues): CustomValues => {
  const extractedValues: CustomValues = {
    subjects: [],
    topics: [],
    yearGroups: [],
    years: [],
    examConditions: [],
    assessmentTypes: []
  };

  // Extract values from assessments
  assessments.forEach((assessment) => {
    if (assessment.subject && !extractedValues.subjects.includes(assessment.subject)) {
      extractedValues.subjects.push(assessment.subject);
    }
    if (assessment.topic && !extractedValues.topics.includes(assessment.topic)) {
      extractedValues.topics.push(assessment.topic);
    }
    if (assessment.yearGroup && !extractedValues.yearGroups.includes(assessment.yearGroup)) {
      extractedValues.yearGroups.push(assessment.yearGroup);
    }
    if (assessment.year && !extractedValues.years.includes(assessment.year)) {
      extractedValues.years.push(assessment.year);
    }
    if (assessment.examCondition && !extractedValues.examConditions.includes(assessment.examCondition)) {
      extractedValues.examConditions.push(assessment.examCondition);
    }
    if (assessment.assessmentType && !extractedValues.assessmentTypes.includes(assessment.assessmentType)) {
      extractedValues.assessmentTypes.push(assessment.assessmentType);
    }
  });

  // Merge extracted values with stored values
  const mergedValues: CustomValues = {
    subjects: [...new Set([...storedCustomValues.subjects, ...extractedValues.subjects])],
    topics: [...new Set([...storedCustomValues.topics, ...extractedValues.topics])],
    yearGroups: [...new Set([...storedCustomValues.yearGroups, ...extractedValues.yearGroups])],
    years: [...new Set([...storedCustomValues.years, ...extractedValues.years])],
    examConditions: [...new Set([...storedCustomValues.examConditions, ...extractedValues.examConditions])],
    assessmentTypes: [...new Set([...storedCustomValues.assessmentTypes, ...extractedValues.assessmentTypes])]
  };

  return mergedValues;
};
